/**
 * SB6-ANA-CACHE-03 — test bridge for the image-cache storage layer.
 *
 * Exposes a thin imperative surface under `window.__aeroCacheBridge` so the
 * Playwright acceptance spec can drive deterministic scenarios that are awkward
 * to provoke through the UI alone: seed manifest datasets, set a tiny test
 * budget, run an eviction pass, read the resulting manifest, and clear. No
 * production code reads this bridge — it is mounted only by `CacheTestBridgeMount`
 * in the analysis surface and removed on unmount.
 *
 * Mirrors the `__aeroUndoBridge` / `__aeroImageStateFilter` precedent.
 */
import { cacheManifest, type DatasetManifest } from "./cacheManifest.js";
import { storageBudget } from "./storageBudget.js";
import { runEviction } from "./lruEviction.js";
import { clearAllImageCache, clearDataset, setPinned } from "./persistence.js";
import { getOpenAcquisitionId } from "./manifestBridge.js";

export interface CacheTestBridge {
  /** Snapshot of every manifest record. */
  list: () => DatasetManifest[];
  /**
   * Seed/replace a dataset record (id, cached image keys, bytes, lastAccess,
   * pin). Resolves once the write is durably flushed to IndexedDB so a test can
   * navigate (full reload) and rely on hydration picking it up.
   */
  seed: (record: {
    dataAcquisitionId: string;
    imageKeys: string[];
    totalBytes: number;
    lastAccess: number;
    pinned?: boolean;
    totalImageCount?: number;
    name?: string;
  }) => Promise<void>;
  /** Record a single cached image (id + key + bytes). */
  record: (id: string, key: string, bytes: number) => void;
  /** Set both budget limits. */
  setBudget: (maxBytes: number, maxUnpinnedDatasets: number) => void;
  /** Pin/unpin a dataset (AC3). */
  pin: (id: string, pinned: boolean) => Promise<void>;
  /** Run one eviction pass; returns the evicted ids. */
  evict: () => Promise<string[]>;
  /** The currently-open acquisition (eviction-exempt). */
  openId: () => string | null;
  /** Clear one dataset. */
  clearOne: (id: string) => Promise<void>;
  /** Clear everything. */
  clearAll: () => Promise<void>;
  /** Await all pending IndexedDB writes (durability barrier before a reload). */
  flush: () => Promise<void>;
}

export function installCacheTestBridge(): () => void {
  const bridge: CacheTestBridge = {
    list: () => cacheManifest.list(),
    seed: async (record) => {
      // Ensure the initial hydration has settled before mutating, so a late
      // hydrate can't interleave with the seed (deterministic for tests).
      await cacheManifest.hydrate();
      // Build a full record via the public mutators so the keySet mirror stays
      // consistent, then force the requested lastAccess + pin.
      cacheManifest.registerDataset(
        record.dataAcquisitionId,
        record.totalImageCount ?? record.imageKeys.length,
        record.name,
      );
      const per = record.imageKeys.length
        ? Math.floor(record.totalBytes / record.imageKeys.length)
        : 0;
      let remaining = record.totalBytes;
      record.imageKeys.forEach((key, i) => {
        const bytes = i === record.imageKeys.length - 1 ? remaining : per;
        remaining -= bytes;
        cacheManifest.recordCachedImage(record.dataAcquisitionId, key, bytes);
      });
      if (record.pinned)
        cacheManifest.setPinned(record.dataAcquisitionId, true);
      // Force the requested recency in the in-memory mirror (matters for the
      // in-memory LRU scenarios), then force-persist so a navigating test's
      // hydration sees the record (the forced lastAccess alone wouldn't trigger
      // a persist through the normal mutators).
      const rec = cacheManifest.get(record.dataAcquisitionId);
      if (rec) rec.lastAccess = record.lastAccess;
      cacheManifest.__persistNow(record.dataAcquisitionId);
      await cacheManifest.flush();
    },
    record: (id, key, bytes) => {
      cacheManifest.recordCachedImage(id, key, bytes);
    },
    setBudget: (maxBytes, maxUnpinnedDatasets) => {
      storageBudget.setMaxBytes(maxBytes);
      storageBudget.setMaxUnpinnedDatasets(maxUnpinnedDatasets);
    },
    pin: async (id, pinned) => {
      await cacheManifest.hydrate();
      await setPinned(id, pinned);
    },
    evict: async () => {
      await cacheManifest.hydrate();
      const result = await runEviction(getOpenAcquisitionId());
      return result.evicted;
    },
    openId: () => getOpenAcquisitionId(),
    clearOne: async (id) => {
      await cacheManifest.hydrate();
      await clearDataset(id);
      await cacheManifest.flush();
    },
    clearAll: async () => {
      await cacheManifest.hydrate();
      await clearAllImageCache();
      await cacheManifest.flush();
    },
    flush: () => cacheManifest.flush(),
  };
  const w = window as unknown as { __aeroCacheBridge?: CacheTestBridge };
  w.__aeroCacheBridge = bridge;
  return () => {
    const w2 = window as unknown as { __aeroCacheBridge?: CacheTestBridge };
    delete w2.__aeroCacheBridge;
  };
}
